/**
 * @fileoverview Monitor ao vivo de uma execução de disparo em massa durável.
 * Mostra contadores (total/pendentes/processando/enviados/entregues/lidos/
 * falhas/ignorados), status, motivo de pausa e próximo retorno, além dos
 * botões Pausar / Continuar / Cancelar. Cada contador é clicável e abre o
 * drawer de detalhes por destinatário. Permite ainda editar o máximo por dia
 * de uma execução já em andamento (o modal só afeta disparos novos). Sem os
 * handlers das ações (quem não gere disparos), só acompanha.
 *
 * Props: { execution, onPause, onResume, onCancel, busy, fetchRecipients, onUpdateCap }
 */

import React from "react";
import { CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Pause, Play, Square, Clock, Pencil, Check, X } from "lucide-react";
import MassSendRecipientsDrawer from "./MassSendRecipientsDrawer";

// Vocabulário REAL de MassSendExecution.status — nada além destes 5 é emitido
// pelo backend (falha é status de destinatário, não da execução).
const STATUS_META = {
  scheduled: { label: "Agendado", color: "#a78bfa" },
  running: { label: "Em andamento", color: "#60a5fa" },
  paused: { label: "Pausado", color: "#f59e0b" },
  completed: { label: "Concluído", color: "#34d399" },
  canceled: { label: "Cancelado", color: "#f87171" },
};

// Motivos de pausa do backend → texto amigável.
const PAUSE_REASON_LABELS = {
  daily_cap: "limite diário atingido",
  daily_cap_connection: "limite diário da conexão atingido",
  daily_cap_company: "limite diário da empresa atingido",
  out_of_window: "fora do horário de envio",
  no_connection: "sem conexão disponível",
  message_quota: "franquia de mensagens do mês esgotada",
  billing_suspended: "envio suspenso pelo atraso no pagamento",
  consecutive_errors: "muitos erros seguidos",
};

const DONE_STATUSES = ["completed", "canceled"];

const fmtDateTime = (v) => {
  if (!v) return null;
  try {
    return new Date(v).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return null;
  }
};

const Tile = ({ label, value, color, statusKey, onClick }) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);
  const clickable = !!onClick && (value || 0) > 0;
  return (
    <div
      onClick={clickable ? () => onClick(statusKey) : undefined}
      title={clickable ? "Ver detalhes" : undefined}
      style={{
        flex: "1 1 60px", minWidth: 60, textAlign: "center", padding: "6px 4px",
        borderRadius: 8, background: W(0.03),
        border: "1px solid " + W(0.06),
        cursor: clickable ? "pointer" : "default",
        transition: "background 0.15s",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: color || W(0.85) }}>
        {(value || 0).toLocaleString("pt-BR")}
      </div>
      <div style={{ fontSize: 9, color: W(0.4), marginTop: 1 }}>{label}</div>
    </div>
  );
};

const actionBtn = (onClick, disabled, icon, label, tone, W) => {
  const palette = {
    amber: { bg: "rgba(245,158,11,0.12)", bd: "rgba(245,158,11,0.35)", fg: "#fbbf24" },
    green: { bg: "rgba(34,197,94,0.12)", bd: "rgba(34,197,94,0.35)", fg: "#4ade80" },
    red: { bg: "rgba(239,68,68,0.12)", bd: "rgba(239,68,68,0.35)", fg: "#f87171" },
  }[tone] || { bg: W(0.04), bd: W(0.12), fg: W(0.7) };
  return (
    <div
      onClick={() => { if (!disabled) onClick(); }}
      style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        padding: "8px 0", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
        background: palette.bg, border: `1px solid ${palette.bd}`, color: palette.fg,
        fontSize: 11, fontWeight: 600, opacity: disabled ? 0.5 : 1, transition: "all 0.15s",
      }}
    >
      {icon}
      {label}
    </div>
  );
};

const MassSendMonitor = ({
  execution, onPause, onResume, onCancel, busy, fetchRecipients, onUpdateCap,
}) => {
  const isDark = useTheme().palette.type === "dark";
  const W = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);

  // drawerStatus: null = fechado; "" = todos; ou a chave de status.
  const [drawerStatus, setDrawerStatus] = React.useState(null);
  const [editingCap, setEditingCap] = React.useState(false);
  const [capInput, setCapInput] = React.useState("");
  const [capBusy, setCapBusy] = React.useState(false);

  if (!execution) return null;

  const status = execution.status || "";
  const meta = STATUS_META[status] || { label: status || "—", color: W(0.6) };
  const isDone = execution.done || DONE_STATUSES.indexOf(status) !== -1;
  const isPaused = status === "paused";
  const isActive = !isDone && !isPaused;
  const c = execution.counts || {};
  const resumeAt = fmtDateTime(execution.nextResumeAt);
  const pauseLabel = execution.pauseReason
    ? PAUSE_REASON_LABELS[execution.pauseReason] || execution.pauseReason
    : null;

  const openDrawer = fetchRecipients ? (key) => setDrawerStatus(key) : undefined;

  const saveCap = () => {
    const v = parseInt(capInput, 10);
    if (!onUpdateCap || !Number.isFinite(v) || v <= 0) {
      setEditingCap(false);
      return;
    }
    setCapBusy(true);
    Promise.resolve(onUpdateCap(v))
      .catch(() => {})
      .finally(() => {
        setCapBusy(false);
        setEditingCap(false);
      });
  };

  return (
    <div
      style={{
        marginTop: 12, padding: "12px", borderRadius: 10,
        background: W(0.02), border: "1px solid " + W(0.08),
      }}
    >
      {/* Cabeçalho: status */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0,
            boxShadow: isActive ? `0 0 0 3px ${meta.color}22` : "none",
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, flex: 1 }}>
          Disparo durável: {meta.label}
        </span>
        {busy && <CircularProgress size={12} style={{ color: W(0.5) }} />}
      </div>

      {/* Motivo de pausa + próximo retorno */}
      {isPaused && (pauseLabel || resumeAt) && (
        <div
          style={{
            marginBottom: 10, padding: "7px 9px", borderRadius: 7,
            background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
            fontSize: 10, color: W(0.7), lineHeight: 1.5,
          }}
        >
          {pauseLabel && (
            <div>
              {execution.isAutoPaused ? "Pausa automática" : "Pausado"}: {pauseLabel}
            </div>
          )}
          {resumeAt && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2, color: "#fbbf24" }}>
              <Clock size={10} /> Retomada prevista: {resumeAt}
            </div>
          )}
        </div>
      )}

      {/* Contadores (clicáveis → drawer de detalhes) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <Tile label="Total" value={c.total} statusKey="" onClick={openDrawer} />
        <Tile label="Pendentes" value={c.pending} color="#9ca3af" statusKey="pending" onClick={openDrawer} />
        <Tile label="Processando" value={c.processing} color="#60a5fa" statusKey="processing" onClick={openDrawer} />
        <Tile label="Enviados" value={c.sent} color="#818cf8" statusKey="sent" onClick={openDrawer} />
        <Tile label="Entregues" value={c.delivered} color="#34d399" statusKey="delivered" onClick={openDrawer} />
        <Tile label="Lidos" value={c.read} color="#22c55e" statusKey="read" onClick={openDrawer} />
        <Tile label="Falhas" value={c.failed} color="#f87171" statusKey="failed" onClick={openDrawer} />
        <Tile label="Ignorados" value={c.ignored} color="#fb923c" statusKey="ignored" onClick={openDrawer} />
        {(c.skipped || 0) > 0 && (
          <Tile label="Pulados" value={c.skipped} color="#fb923c" statusKey="skipped" onClick={openDrawer} />
        )}
      </div>

      {/* Máximo por dia (editável em execução ativa) */}
      {!isDone && onUpdateCap && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 11, color: W(0.55) }}>
          <span>Máx. por dia:</span>
          {editingCap ? (
            <>
              <input
                value={capInput}
                autoFocus
                onChange={(e) => setCapInput(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") saveCap(); if (e.key === "Escape") setEditingCap(false); }}
                style={{
                  width: 64, padding: "3px 6px", borderRadius: 6, fontSize: 11,
                  background: W(0.05), border: "1px solid " + W(0.15), color: W(0.9), outline: "none",
                }}
              />
              {capBusy ? (
                <CircularProgress size={12} style={{ color: W(0.5) }} />
              ) : (
                <>
                  <span onClick={saveCap} style={{ cursor: "pointer", color: "#4ade80", display: "flex" }} title="Salvar">
                    <Check size={15} />
                  </span>
                  <span onClick={() => setEditingCap(false)} style={{ cursor: "pointer", color: "#f87171", display: "flex" }} title="Cancelar">
                    <X size={15} />
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <b style={{ color: W(0.85) }}>
                {execution.maxSendsPerDay != null ? execution.maxSendsPerDay.toLocaleString("pt-BR") : "—"}
              </b>
              <span
                onClick={() => { setCapInput(String(execution.maxSendsPerDay || "")); setEditingCap(true); }}
                style={{ cursor: "pointer", color: W(0.45), display: "flex" }}
                title="Editar máximo por dia"
              >
                <Pencil size={12} />
              </span>
            </>
          )}
        </div>
      )}

      {/* Ações */}
      {!isDone && onCancel && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {isActive &&
            actionBtn(() => onPause(execution.id), busy, <Pause size={13} />, "Pausar", "amber", W)}
          {isPaused &&
            actionBtn(() => onResume(execution.id), busy, <Play size={13} />, "Continuar", "green", W)}
          {actionBtn(() => onCancel(execution.id), busy, <Square size={13} />, "Cancelar", "red", W)}
        </div>
      )}

      {/* Drawer de detalhes por destinatário */}
      <MassSendRecipientsDrawer
        open={drawerStatus !== null}
        executionId={execution.id}
        status={drawerStatus || undefined}
        onClose={() => setDrawerStatus(null)}
        fetchRecipients={fetchRecipients}
      />
    </div>
  );
};

export default MassSendMonitor;
